import { useState } from "react";
import { ResourceFile } from "./types";
import { 
  isQuizFile, 
  isFreeResponseFile, 
  isDragDropFile, 
  isLinkingFile, 
  determineResourceType
} from "./utils/fileTypeUtils";
import { PDFContentViewer } from "./content-viewers/PDFContentViewer";
import { VideoContentViewer } from "./content-viewers/VideoContentViewer";
import { QuizContentTypeHandler } from "./content-viewers/handlers/QuizContentTypeHandler";
import { InteractiveContentViewer } from "./content-viewers/InteractiveContentViewer";
import { EmptyContentViewer } from "./content-viewers/EmptyContentViewer";
import { ErrorContentViewer } from "./content-viewers/ErrorContentViewer";
import { useQuizData } from "./useQuizData";

interface ResourceContentViewerProps {
  currentFile: ResourceFile | undefined;
  resourceId: string;
  isTrainee?: boolean;
  isAdmin?: boolean;
  onContentComplete?: () => void;
  loadingError: string | null;
}

export function ResourceContentViewer({
  currentFile,
  resourceId,
  isTrainee = false,
  isAdmin = false,
  onContentComplete,
  loadingError
}: ResourceContentViewerProps) {
  const {
    quiz,
    isLoadingQuiz,
    quizCompleted,
    quizAttempt,
    updateQuizCompletion,
    error: quizError
  } = useQuizData(resourceId);

  if (!currentFile) {
    return <EmptyContentViewer />;
  }

  if (loadingError) {
    return (
      <ErrorContentViewer
        message={loadingError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const isQuizView = isQuizFile(currentFile);
  const isFreeResponseView = isFreeResponseFile(currentFile);
  const isDragDropView = isDragDropFile(currentFile);
  const isLinkingView = isLinkingFile(currentFile);

  // Handle interactive content types
  if (isQuizView) {
    return (
      <QuizContentTypeHandler
        currentFile={currentFile}
        currentFileIndex={0}
        files={[currentFile]}
        completedFiles={{}}
        isAdmin={isAdmin}
        isTrainee={isTrainee}
        isFileLocked={() => false}
        overallProgress={100}
        resourceId={resourceId}
        quiz={quiz}
        isLoadingQuiz={isLoadingQuiz}
        quizCompleted={quizCompleted}
        quizAttempt={quizAttempt}
        handleQuizComplete={updateQuizCompletion}
        onComplete={onContentComplete}
        error={quizError}
      />
    );
  }

  if (isFreeResponseView || isDragDropView || isLinkingView) {
    let type: 'free_response' | 'drag_drop' | 'linking' = 'free_response';
    
    if (isDragDropView) type = 'drag_drop';
    if (isLinkingView) type = 'linking';
    
    return (
      <InteractiveContentViewer
        type={type}
        fileId={currentFile.file_id || currentFile.id}
        resourceId={resourceId}
        onComplete={onContentComplete}
      />
    );
  }

  const fileUrl = currentFile.file_url || '';
  
  // Special case handling for specific Supabase video files
  if (fileUrl.toLowerCase().includes('supabase.co/storage') && 
     (fileUrl.toLowerCase().includes('5094134-hd_1920_1080_30fps') || 
      fileUrl.toLowerCase().includes('test-content'))) {
    console.log('Forcing video type for specific Supabase file:', fileUrl);
    
    return (
      <VideoContentViewer
        fileUrl={fileUrl} 
        fileId={currentFile.id}
        resourceId={resourceId}
        onComplete={onContentComplete} 
      />
    );
  }
  
  // Determine the file type and render the appropriate viewer
  let fileType = currentFile.resourceType || determineResourceType(currentFile.file_url, currentFile.file_type);
  
  // Enhanced video detection for Supabase URLs
  const isSupabaseUrl = fileUrl.includes('supabase.co/storage');
  const isVideoFileName = fileUrl.toLowerCase().includes('.mp4') || 
                         fileUrl.toLowerCase().includes('.webm') ||
                         fileUrl.toLowerCase().includes('.mov');
  const isVideoType = currentFile.file_type === 'video' || 
                     currentFile.file_type === 'mp4' || 
                     currentFile.file_type === 'webm' || 
                     currentFile.file_type === 'mov';
                    
  if (isSupabaseUrl && (isVideoFileName || isVideoType)) {
    console.log('Forcing video type for Supabase URL:', fileUrl);
    fileType = 'video';
  }
  
  console.log('ResourceContentViewer analysis:', {
    title: currentFile.title,
    url: fileUrl,
    type: fileType,
    fileType: currentFile.file_type,
    isSupabaseUrl,
    isVideoFileName,
    isVideoType
  });

  // Render the appropriate viewer based on file type
  if (fileType === 'pdf') {
    return (
      <PDFContentViewer
        fileUrl={fileUrl}
        isTrainee={isTrainee}
        isAdmin={isAdmin}
        onComplete={onContentComplete}
      />
    );
  }
  
  if (fileType === 'video') {
    console.log('Rendering video for:', fileUrl);
    return (
      <VideoContentViewer 
        fileUrl={fileUrl} 
        fileId={currentFile.id}
        resourceId={resourceId}
        onComplete={onContentComplete} 
      />
    );
  }

  // Fallback for unsupported content types
  return (
    <ErrorContentViewer
      message={`This file type (${fileUrl?.split('.').pop() || currentFile.file_type || 'unknown'}) is not supported in the current view.`}
      details={`File URL: ${fileUrl || 'None'}\nDetected type: ${fileType}\nProvided type: ${currentFile.file_type || 'None'}\nIs Supabase: ${isSupabaseUrl}\nIs Video Filename: ${isVideoFileName}\nIs Video Type: ${isVideoType}`}
      onRetry={() => window.location.reload()}
    />
  );
}
